import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Code,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Search,
  Zap,
  Layout,
  Smartphone,
  ShoppingBag,
  Database,
  Lock,
  Cpu,
  Monitor,
} from "lucide-react";

export const Route = createFileRoute("/services_/web-development")({
  head: () => ({
    meta: [
      {
        title:
          "Website Design & Development Services — KMWAdvertising · UI/UX & eCommerce",
      },
      {
        name: "description",
        content:
          "Website Design & Development Services That Create Exceptional Digital Experiences and Drive Business Growth. Responsive Web, React, WordPress & Shopify by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Website Design & Development Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Build a Website That Looks Great, Performs Better and Converts Visitors. Mobile-first responsive UI/UX, Core Web Vitals, and landing page engineering.",
      },
    ],
  }),
  component: WebDevelopmentServicePage,
});

export function WebDevelopmentServicePage() {
  const whyWebsiteMatters = [
    {
      title: "Build Brand Credibility",
      desc: "A modern, custom-designed website establishes instant consumer trust, showcases authority, and reinforces brand identity.",
      icon: Award,
    },
    {
      title: "Generate Qualified Leads",
      desc: "Intuitive UI/UX architecture and conversion-rate optimization (CRO) guide visitors toward inquiries, bookings, and sales.",
      icon: Target,
    },
    {
      title: "Search Engine Discoverability",
      desc: "SEO-first coding standards, clean URLs, and structured schema markup ensure top indexation across Google and search engines.",
      icon: Search,
    },
    {
      title: "High Performance & Core Web Vitals",
      desc: "Ultra-fast load speeds, mobile responsiveness, and clean codebase reduce bounce rates and maximize user engagement.",
      icon: Zap,
    },
    {
      title: "Central Digital Hub",
      desc: "Serves as the high-converting destination for all SEO, Google Ads, SMM, Email, and Performance Marketing campaigns.",
      icon: Globe,
    },
  ];

  const designAndDevServices = [
    {
      title: "Custom UI/UX & Visual Design",
      desc: "Custom user interfaces, wireframes, interactive prototypes, visual branding, and design systems crafted for optimal conversion.",
      icon: Layout,
    },
    {
      title: "Responsive Mobile-First Development",
      desc: "Mobile-first layouts, cross-browser compatibility, WCAG accessibility, and high-performance frontend coding.",
      icon: Smartphone,
    },
    {
      title: "WordPress & CMS Solutions",
      desc: "Scalable custom WordPress theme development, plugin integration, CMS configuration, speed tuning, and security hardening.",
      icon: Database,
    },
    {
      title: "eCommerce Store Development",
      desc: "Shopify, WooCommerce, and custom web stores with fast checkout flows, payment gateway integrations, and ERP inventory sync.",
      icon: ShoppingBag,
    },
    {
      title: "SEO-Friendly Web Architecture",
      desc: "Clean URL routing, heading hierarchy, Schema markup, XML sitemaps, image compression, and Core Web Vitals optimization.",
      icon: Code,
    },
    {
      title: "Continuous Maintenance & Support",
      desc: "Security updates, performance monitoring, automated backups, content updates, bug fixes, and technical health audits.",
      icon: ShieldCheck,
    },
  ];

  const techStackList = [
    "HTML5 & Modern CSS3",
    "JavaScript (ES6+) & TypeScript",
    "React & Next.js Frameworks",
    "Node.js & Express REST APIs",
    "PHP & Custom Laravel Development",
    "WordPress & WooCommerce Ecosystem",
    "Shopify & Storefront APIs",
    "MySQL, PostgreSQL & MongoDB",
    "Tailwind CSS & Modern Design Tokens",
    "Cloud Hosting (AWS, Vercel, Netlify)",
  ];

  const webPlanningSteps = [
    "Commercial requirement & business goals analysis",
    "Target user persona & competitor benchmarking",
    "Information architecture & sitemap mapping",
    "Low-fidelity wireframing & UX user flows",
    "High-fidelity UI designs & interactive prototypes",
    "Clean-code development & API integrations",
    "Rigorous cross-device & speed testing",
    "Launch deployment, SEO audit & ongoing maintenance",
  ];

  const industriesServed = [
    "FMCG & Consumer Brands",
    "Healthcare & Hospitals",
    "Manufacturing & Industrial B2B",
    "Real Estate & Property Developers",
    "Financial Services & Banking",
    "Higher Education & EdTech",
    "Hospitality & Hotels",
    "Retail & E-Commerce Stores",
    "Automobile Dealerships & Mobility",
    "Media, Entertainment & Publishing",
    "Professional Services & Consulting",
  ];

  const kmwaStrengths = [
    "25+ years of strategic advertising, branding & media heritage",
    "In-house senior UI/UX designers & full-stack engineers",
    "SEO-first development methodology built from day one",
    "Mobile-first responsive layouts tailored for all screen sizes",
    "Conversion-focused layout engineering & CRO principles",
    "Ultra-fast page speed & Core Web Vitals optimization",
    "Seamless integration with Google Analytics, CRMs & Ad Pixels",
    "Scalable, enterprise-ready website architecture",
    "Transparent project management & dedicated support",
    "Long-term maintenance & digital growth partnership",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">Website Design & Development</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Code className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Website Design & Development Services That Create Exceptional Digital Experiences and Drive Business Growth
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                Website Design, UI/UX Design, Web Development & Landing Pages
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Build a Website That Looks Great, Performs Better and Converts Visitors into Customers
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Your website is often the first interaction customers have with your business. Within seconds, visitors form opinions based on design, usability, speed and credibility. A professionally designed website not only strengthens your brand identity but serves as a high-converting digital foundation.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Modern websites must do much more than display static information. They need to deliver seamless UI/UX user experiences, load instantly across mobile devices, rank near the top of search engines, and integrate smoothly with CRM and marketing platforms.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we create responsive, SEO-friendly and performance-driven websites combining 25+ years of advertising, design, and full-stack engineering expertise across Corporate, eCommerce, Landing Pages, and Enterprise solutions.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Web Development Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY YOUR WEBSITE MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Digital Core Asset
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Your Website Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                A website is more than an online brochure—it is the central digital hub of your brand and one of your most valuable commercial assets.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whyWebsiteMatters.map((item, idx) => {
                const WIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <WIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SERVICES SUITES & DEVELOPMENT CAPABILITIES */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                End-to-End Web Solutions
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Website Design & Development Services
              </h2>
            </div>

            {/* 6 SERVICES CARDS */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {designAndDevServices.map((svc, idx) => {
                const SIcon = svc.icon;
                return (
                  <div key={idx} className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-4">
                    <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                      <SIcon className="h-5 w-5" />
                    </div>
                    <h4 className="font-display text-xl font-bold text-white">{svc.title}</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{svc.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* TECH STACK & PLANNING STEPS */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Tech Stack */}
              <div className="lg:col-span-5 p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Monitor className="h-5 w-5 text-[var(--glow-purple)]" />
                  Technologies We Work With
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Modern, clean-code web technologies selected based on your scalability, speed, and business requirements:
                </p>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  {techStackList.map((tech, i) => (
                    <div key={i} className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90 font-medium">
                      {tech}
                    </div>
                  ))}
                </div>
              </div>

              {/* Planning Process */}
              <div className="lg:col-span-7 p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Layout className="h-5 w-5 text-sky-400" />
                  Our Web Planning & Launch Methodology
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  A structured design and development process ensuring quality, speed, and zero post-launch bugs:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {webPlanningSteps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INDUSTRIES WE SERVE */}
        <section className="py-16 md:py-24 border-b border-white/10 relative bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Cross-Industry Web Solutions
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Industries We Serve
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {industriesServed.map((ind, i) => (
                <div key={i} className="p-3.5 rounded-xl glass border border-white/10 text-xs text-white/90 font-medium flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                  <span>{ind}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                25+ Years Advertising & Web Heritage
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Code className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              Website Engineering Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Build an Exceptional Website That Converts?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Contact our web design and development team today to build a responsive, fast, and SEO-friendly website tailored to your commercial goals.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Web Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
